import { Type, type Static } from '@sinclair/typebox';
import { AttachmentSchema } from '../../shared/attachment/attachment.schema';
import { reactionCounterSchema } from '../../shared/reaction/reaction-counter.schema';
import { MinimalUser, minimalUserSchema } from '../../shared/user/user.schema';
import { Post, PostPreview, PostPreviewSchema, PostSchema } from '../post/post.schema';

export const CommentSchema = Type.Object({
  object: Type.Literal('Comment'),
  id: Type.String({ description: 'The ID of the comment.' }),
  thread_id: Type.Optional(Type.String({ description: 'The ID of the thread if this is a reply to another comment.' })),
  author: Type.Object(minimalUserSchema, { description: 'The author of the comment.' }),
  created_at: Type.String({
    description: 'The creation date of the comment. Uses ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SS.sssZ).',
  }),
  text: Type.String({ description: 'The text content of the comment.' }),
  attachments: Type.Array(AttachmentSchema, { description: 'List of comment attachments.' }),
  is_sender: Type.Boolean({ description: 'Is the current user the sender of the comment.' }),
  can_reply: Type.Boolean({ description: 'Whether the current user can reply to the comment or not.' }),
  can_react: Type.Boolean({ description: 'Whether the current user can react to the comment or not.' }),
  reply_counter: Type.Number({ description: 'The number of replies to the comment.' }),
  impressions_counter: Type.Optional(Type.Number({ description: 'The number of impressions of the comment.' })),
  reactions_counter: reactionCounterSchema,
});

export const CommentWithParentPostSchema = Type.Composite([
  CommentSchema,
  Type.Object({ parent_post: Type.Union([PostPreviewSchema, PostSchema, Type.Null()]) }),
]);

export type Comment<UserSpecifics = unknown> = Omit<Static<typeof CommentSchema>, 'author'> & {
  author: MinimalUser<UserSpecifics>;
};

export type CommentWithParentPost<UserSpecifics = unknown> = Omit<Static<typeof CommentWithParentPostSchema>, 'author'> & {
  author: MinimalUser<UserSpecifics>;
  parent_post: Post<UserSpecifics> | PostPreview<UserSpecifics> | null;
};
